'use client';

import {
  Icons,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@commenda/kakashi';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

export interface BindingConfig {
  sourceField: string;
  disableOnBind?: boolean;
}

export function BindingsEditor({
  bindings,
  availableFields,
  onChange,
}: {
  bindings?: BindingConfig;
  availableFields: { fieldId: string; label: string }[];
  onChange: (bindings: BindingConfig | undefined) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(!!bindings?.sourceField);

  const sourceField = bindings?.sourceField || '';
  const disableOnBind = bindings?.disableOnBind ?? false;
  const hasBinding = !!sourceField;

  const handleEnableBinding = (enabled: boolean) => {
    if (enabled && availableFields.length > 0) {
      onChange({
        sourceField: availableFields[0].fieldId,
        disableOnBind: false,
      });
    } else {
      onChange(undefined);
    }
    setIsExpanded(enabled);
  };

  const handleSourceChange = (source: string) => {
    if (source) {
      onChange({ sourceField: source, disableOnBind });
    } else {
      onChange(undefined);
    }
  };

  const handleDisableToggle = (disabled: boolean) => {
    if (sourceField) {
      onChange({ sourceField, disableOnBind: disabled });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-text-tertiary" />
          ) : (
            <ChevronRight className="w-4 h-4 text-text-tertiary" />
          )}
          <h3 className="txt-small-medium text-text-secondary">
            Field Binding
          </h3>
        </button>
        <Switch checked={hasBinding} onCheckedChange={handleEnableBinding} />
      </div>

      {isExpanded && (
        <div className="space-y-3 pl-6 border-l-2 border-border-secondary">
          <p className="txt-xs-regular text-text-tertiary">
            Pre-fill this field with the value from another field
          </p>

          {availableFields.length > 0 ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Copy value from</Label>
                <Select value={sourceField} onValueChange={handleSourceChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source field" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFields.map((f) => (
                      <SelectItem key={f.fieldId} value={f.fieldId}>
                        {f.label} ({f.fieldId})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasBinding && (
                <>
                  <div className="flex items-center justify-between py-2 px-3 bg-bg-secondary rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icons.Lock01 className="w-4 h-4 text-text-tertiary" />
                      <span className="txt-small-regular text-text-secondary">
                        Make field read-only
                      </span>
                    </div>
                    <Switch
                      checked={disableOnBind}
                      onCheckedChange={handleDisableToggle}
                    />
                  </div>

                  <div className="p-3 bg-bg-info-subtle rounded-lg">
                    <p className="txt-xs-regular text-text-info">
                      <Icons.InfoCircle className="w-3 h-3 inline mr-1" />
                      When "
                      {availableFields.find((f) => f.fieldId === sourceField)
                        ?.label || sourceField}
                      " changes, this field will be automatically updated.
                      {disableOnBind &&
                        ' The field will be disabled so users cannot edit it.'}
                    </p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="txt-xs-regular text-text-tertiary">
              Add other fields to the form to create bindings
            </p>
          )}
        </div>
      )}
    </div>
  );
}
